import React, { Fragment } from 'react'
import { useMedia } from 'sb-hooks'
import PropTypes from 'prop-types'
import cx from 'classnames'

import Text from 'components/_new/typography/Text/Text'
import { Image, Price, OldAndNewPrices } from 'components/_new/ui'
import { Box } from 'components/_new/layout'

import messages from './messages'
import s from './ActiveVolumeInfo.scss'


const getTitleMessage = (values) => {
  const { isSubscription, isPerfumeGroup, isPerfumeBottle, isPriceWithDiscountVisible } = values

  if (isSubscription) {
    return messages.titles.subscription
  }

  if (isPerfumeGroup) {
    return isPerfumeBottle
      ? messages.titles.perfume
      : messages.titles.alaCarte
  }

  return isPriceWithDiscountVisible
    ? messages.titles.discount
    : messages.titles.alaCarte
}

const getDescriptionMessage = (values) => {
  const {
    isPerfumeBottle, isSubscription, isMakeup, isExtras,
    isSkincare, isPersonalCare, isCandle, isPerfumeGroup,
  } = values

  if (isPerfumeBottle || isExtras) {
    return null
  }

  if (isPerfumeGroup) {
    return messages.descriptions.fragrance
  }

  if (isCandle) {
    return messages.descriptions.candle
  }

  if (isMakeup || isPersonalCare || (isSubscription && isSkincare)) {
    return messages.descriptions.fullSized
  }

  return messages.descriptions.delivery
}

const ActiveVolumeInfo = ({ className, data, priceTitle, priceSubTitle, withVolume }) => {
  const { isMobile } = useMedia()

  // TODO need to check it by other param bcs data isn't empty anyway - see component usage
  if (!data) {
    return null
  }

  const { img, price, priceWithDiscount, actualType, type,
    inStock, productGroup, upchargePrice, volume, volumeUnit } = data

  const isSubscription  = type === 'subscription'
  const isPerfumeBottle = actualType === 'PerfumeBottle'
  const isPerfumeGroup  = productGroup === 'Perfume'
  const isSkincare      = productGroup === 'BathBody'
  const isCandle        = productGroup === 'HomeFragrance'
  const isPersonalCare  = productGroup === 'PersonalCare'
  const isMakeup        = (
    productGroup === 'Makeup'
    || productGroup === 'EyeLiner'
    || productGroup === 'LipLiner'
    || productGroup === 'LipCrayon'
  )
  const isExtras = (
    productGroup === 'CandleSet'
    || productGroup === 'PerfumeCase'
    || productGroup === 'FragranceStorageBox'
  )

  if (isMakeup) {
    withVolume = false
  }

  const isPriceWithDiscountVisible = priceWithDiscount && priceWithDiscount < price

  const priceTitleMessage = getTitleMessage({
    isSubscription, isPerfumeGroup, isPerfumeBottle, isPriceWithDiscountVisible,
  })

  const descriptionMessage = inStock && getDescriptionMessage({
    isPerfumeBottle, isSubscription, isMakeup, isExtras,
    isSkincare, isPersonalCare, isCandle, isPerfumeGroup,
  })

  const rootClassName = cx(s.activeVolumeInfo, className)
  const textProps     = isMobile ? { size: '18-24', semibold: true } : {}

  return (
    <div className={rootClassName}>
      {
        Boolean(img) && (
          <Box className={s.preview} mr={12} alignCenter>
            <Image src={img} h={isMobile ? 100 : 50} x2 x3 />
          </Box>
        )
      }
      <div className={s.textContainer}>
        {
          inStock ? (
            <div>
              <Text
                className={s.priceTitle}
                message={priceTitle || priceTitleMessage}
                tag="span"
                html
                {...textProps}
              />
              {
                isPriceWithDiscountVisible ? (
                  <Text tag="span" {...textProps}>
                    <OldAndNewPrices
                      oldPrice={price}
                      newPrice={priceWithDiscount}
                      isCents
                      dataTestId="oldAndNewPrices"
                    />
                  </Text>
                ) : (
                  <Text {...textProps} tag="span" color="brand" semibold>
                    <Price value={price} dataTestId="price" isCents />
                  </Text>
                )
              }
            </div>
          ) : (
            <Text
              message={messages.outOfStock}
              color="brand"
              uppercase
              semibold
            />
          )
        }
        {
          Boolean(upchargePrice && isSubscription && inStock) && (
            <Fragment>
              <Text
                className={s.priceTitle}
                message={messages.upchargePrice}
                tag="span"
                {...textProps}
              />
              <Text {...textProps} color="brand" tag="span" semibold>
                +<Price value={upchargePrice} isCents />
              </Text>
            </Fragment>
          )
        }
        {
          Boolean(priceSubTitle) && (
            <Text className={s.desc} message={priceSubTitle} color="gray" />
          )
        }
        {
          Boolean(descriptionMessage) && (
            <Text
              className={s.desc}
              message={{ ...descriptionMessage, values: { volume: withVolume ? `${volume} ${volumeUnit}` : '' } }}
              color="gray"
              html
            />
          )
        }
      </div>
    </div>
  )
}

ActiveVolumeInfo.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape({
    img: PropTypes.string,
    price: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]).isRequired,
    volume: PropTypes.oneOfType([ PropTypes.number, PropTypes.string ]),
    type: PropTypes.string,
    upchargePrice: PropTypes.number,
    inStock: PropTypes.bool.isRequired,
    limitedEditionVolumeCustomText: PropTypes.string,
  }),
  priceTitle: PropTypes.string,
  priceSubTitle: PropTypes.string,
  withVolume: PropTypes.bool,
}

ActiveVolumeInfo.defaultProps = {
  withVolume: true,
}


export default ActiveVolumeInfo
